
import React, { useCallback, useEffect, useState } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import axios from "../../utils/axios";

const emptyForm = {
  name: "",
  code: "",
  description: "",
  is_active: true,
};

const ProcurementCategories = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get("/procurement-categories", {
        params: {
          search,
          status,
          page,
          per_page: 10,
        },
      });

      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Unable to load categories."
        );
      }

      const result = response.data.data;
      const items = Array.isArray(result)
        ? result
        : result?.data || [];

      setCategories(items);
      setLastPage(result?.last_page || 1);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load procurement categories."
      );
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  const fetchStats = useCallback(async () => {
    try {
      const [activeResponse, inactiveResponse] = await Promise.all([
        axios.get("/procurement-categories", {
          params: { status: "active", per_page: 1 },
        }),
        axios.get("/procurement-categories", {
          params: { status: "inactive", per_page: 1 },
        }),
      ]);

      const activeResult = activeResponse.data?.data;
      const inactiveResult = inactiveResponse.data?.data;

      const active =
        activeResult?.total ??
        (Array.isArray(activeResult) ? activeResult.length : 0);

      const inactive =
        inactiveResult?.total ??
        (Array.isArray(inactiveResult) ? inactiveResult.length : 0);

      setStats({
        total: active + inactive,
        active,
        inactive,
      });
    } catch {
      // Keep the page usable if the statistics request fails.
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const updateField = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setValidationErrors((previous) => ({ ...previous, [key]: null }));
    setError("");
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setValidationErrors({});
    setError("");
    setModalOpen(true);
  };

  const openEdit = (category) => {
    setEditing(category);
    setForm({
      name: category.name || "",
      code: category.code || "",
      description: category.description || "",
      is_active: Boolean(category.is_active),
    });
    setValidationErrors({});
    setError("");
    setModalOpen(true);
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setValidationErrors({});

    try {
      if (editing) {
        await axios.put(
          `/procurement-categories/${editing.id}`,
          form
        );
      } else {
        await axios.post("/procurement-categories", form);
      }

      setModalOpen(false);
      setEditing(null);
      setForm(emptyForm);

      await Promise.all([fetchCategories(), fetchStats()]);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to save procurement category."
      );

      setValidationErrors(err.response?.data?.errors || {});
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async () => {
    if (!confirmDelete) return;

    setSaving(true);
    setError("");

    try {
      await axios.delete(
        `/procurement-categories/${confirmDelete.id}`
      );

      setConfirmDelete(null);
      await Promise.all([fetchCategories(), fetchStats()]);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to delete procurement category."
      );
      setConfirmDelete(null);
    } finally {
      setSaving(false);
    }
  };

  const fieldClass =
    "w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20";

  const inputError = (field) =>
    validationErrors[field]?.[0] || "";

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        <Sidebar
          isCollapsed={collapsed}
          onToggleCollapse={() => setCollapsed((value) => !value)}
        />

        <main
          className={`mt-16 min-w-0 flex-1 transition-layout ${
            collapsed ? "ml-16" : "ml-72"
          }`}
        >
          <div className="p-5 md:p-8">
            <Breadcrumb />

            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Icon
                    name="Tags"
                    size={24}
                    className="text-primary"
                  />
                </div>

                <div>
                  <h1 className="text-2xl font-bold text-foreground md:text-3xl">
                    Procurement Categories
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Organize procurement requests using reusable categories.
                  </p>
                </div>
              </div>

              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={openCreate}
              >
                Add Category
              </Button>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-2 rounded-lg border border-error/20 bg-error/10 p-4 text-sm text-error">
                <Icon name="AlertCircle" size={18} />
                <span>{error}</span>
                <button
                  type="button"
                  className="ml-auto"
                  onClick={() => setError("")}
                  aria-label="Dismiss error"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            )}

            <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {[
                {
                  label: "Total Categories",
                  value: stats.total,
                  icon: "Tags",
                  color: "bg-primary/10 text-primary",
                },
                {
                  label: "Active Categories",
                  value: stats.active,
                  icon: "CheckCircle2",
                  color: "bg-success/10 text-success",
                },
                {
                  label: "Inactive Categories",
                  value: stats.inactive,
                  icon: "CirclePause",
                  color: "bg-warning/10 text-warning",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-border bg-card p-5"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {item.label}
                      </p>
                      <p className="mt-2 text-3xl font-bold text-foreground">
                        {item.value}
                      </p>
                    </div>
                    <div
                      className={`flex h-11 w-11 items-center justify-center rounded-lg ${item.color}`}
                    >
                      <Icon name={item.icon} size={21} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border p-5">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    Category Directory
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Create, update and manage procurement categories.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setStatus("all");
                    setPage(1);
                  }}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Reset filters
                </button>
              </div>

              <div className="flex flex-col gap-3 border-b border-border p-5 md:flex-row">
                <div className="relative min-w-0 flex-1">
                  <Icon
                    name="Search"
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder="Search name, code or description..."
                    className={`${fieldClass} pl-9`}
                  />
                </div>

                <select
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value);
                    setPage(1);
                  }}
                  className={`${fieldClass} md:w-48`}
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>

                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={() => {
                    fetchCategories();
                    fetchStats();
                  }}
                >
                  Refresh
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/20">
                      {[
                        "Category",
                        "Code",
                        "Description",
                        "Status",
                        "Created",
                        "Actions",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-5 py-16 text-center text-sm text-muted-foreground"
                        >
                          <Icon
                            name="LoaderCircle"
                            size={22}
                            className="mx-auto mb-3 animate-spin"
                          />
                          Loading procurement categories...
                        </td>
                      </tr>
                    ) : categories.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-16 text-center">
                          <Icon
                            name="Tags"
                            size={30}
                            className="mx-auto text-muted-foreground"
                          />
                          <p className="mt-3 font-semibold text-foreground">
                            No categories found
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Create a category or change your filters.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      categories.map((category) => (
                        <tr
                          key={category.id}
                          className="border-b border-border last:border-0 hover:bg-muted/20"
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                <Icon name="Folder" size={19} />
                              </div>
                              <span className="font-medium text-foreground">
                                {category.name}
                              </span>
                            </div>
                          </td>

                          <td className="px-5 py-4">
                            <span className="rounded-md bg-muted px-2 py-1 font-mono text-xs">
                              {category.code}
                            </span>
                          </td>

                          <td className="max-w-xs px-5 py-4 text-sm text-muted-foreground">
                            <span className="line-clamp-2">
                              {category.description || "—"}
                            </span>
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${
                                category.is_active
                                  ? "border-success/20 bg-success/10 text-success"
                                  : "border-border bg-muted text-muted-foreground"
                              }`}
                            >
                              {category.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-sm text-muted-foreground">
                            {category.created_at
                              ? new Date(
                                  category.created_at
                                ).toLocaleDateString("en-IN")
                              : "—"}
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                title="Edit category"
                                onClick={() => openEdit(category)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
                              >
                                <Icon name="Pencil" size={17} />
                              </button>

                              <button
                                type="button"
                                title="Delete category"
                                onClick={() => setConfirmDelete(category)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-error hover:bg-error/10"
                              >
                                <Icon name="Trash2" size={17} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {lastPage}
                </p>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((value) => value - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    disabled={page >= lastPage || loading}
                    onClick={() => setPage((value) => value + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close modal"
            onClick={() => !saving && setModalOpen(false)}
            className="absolute inset-0 bg-black/50"
          />

          <div className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl border border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border p-5">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {editing ? "Edit Category" : "Add Procurement Category"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter the category details below.
                </p>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={() => setModalOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted"
              >
                <Icon name="X" size={19} />
              </button>
            </div>

            <form onSubmit={saveCategory} className="space-y-5 p-5">
              {error && (
                <div className="rounded-lg border border-error/20 bg-error/10 p-3 text-sm text-error">
                  {error}
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-medium">
                  Category Name *
                </label>
                <input
                  required
                  maxLength={150}
                  value={form.name}
                  onChange={(event) =>
                    updateField("name", event.target.value)
                  }
                  placeholder="e.g. IT Hardware"
                  className={fieldClass}
                />
                {inputError("name") && (
                  <p className="mt-1 text-xs text-error">
                    {inputError("name")}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">
                  Category Code *
                </label>
                <input
                  required
                  maxLength={50}
                  pattern="[A-Za-z0-9_-]+"
                  value={form.code}
                  onChange={(event) =>
                    updateField(
                      "code",
                      event.target.value.toUpperCase().replace(/\s/g, "-")
                    )
                  }
                  placeholder="e.g. IT-HARDWARE"
                  className={fieldClass}
                />
                {inputError("code") && (
                  <p className="mt-1 text-xs text-error">
                    {inputError("code")}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">
                  Description
                </label>
                <textarea
                  rows={4}
                  maxLength={5000}
                  value={form.description}
                  onChange={(event) =>
                    updateField("description", event.target.value)
                  }
                  placeholder="Describe this procurement category..."
                  className={`${fieldClass} resize-y`}
                />
                {inputError("description") && (
                  <p className="mt-1 text-xs text-error">
                    {inputError("description")}
                  </p>
                )}
              </div>

              <label className="flex items-center gap-3 rounded-lg border border-border p-4">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(event) =>
                    updateField("is_active", event.target.checked)
                  }
                  className="h-4 w-4 accent-primary"
                />
                <span>
                  <span className="block text-sm font-medium">
                    Active category
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Active categories can be selected for new procurement requests.
                  </span>
                </span>
              </label>

              <div className="flex justify-end gap-3 border-t border-border pt-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  iconName={saving ? "LoaderCircle" : "Save"}
                  iconPosition="left"
                >
                  {saving ? "Saving..." : "Save Category"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close confirmation"
            onClick={() => !saving && setConfirmDelete(null)}
            className="absolute inset-0 bg-black/50"
          />

          <div className="relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-error/10 text-error">
              <Icon name="Trash2" size={22} />
            </div>

            <h2 className="mt-4 text-lg font-semibold text-foreground">
              Delete Procurement Category?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Are you sure you want to delete{" "}
              <strong>{confirmDelete.name}</strong>? This action cannot be undone.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={saving}
                onClick={deleteCategory}
              >
                {saving ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcurementCategories;
